import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { ArrowDown, ArrowUp, ArrowsLeftRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const TX_LABELS = {
  purchase: { label: "Bought Coins", icon: ArrowDown, cls: "text-emerald-400" },
  gift_sent: { label: "Sent gift", icon: ArrowUp, cls: "text-papaya" },
  gift_received: { label: "Pearls from gift", icon: ArrowDown, cls: "text-pink-400" },
  boost: { label: "Boosted post", icon: ArrowUp, cls: "text-sky-400" },
  subscribe: { label: "Subscribed", icon: ArrowUp, cls: "text-violet-400" },
  subscriber_pearls: { label: "Subscriber pearls", icon: ArrowDown, cls: "text-pink-400" },
  cashout_request: { label: "Cashout requested", icon: ArrowsLeftRight, cls: "text-amber-400" },
};

export default function Wallet() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/wallet");
    setData(data);
    setSelected((cur) => cur || data.packages?.[1] || null);
  }, []);

  useEffect(() => { load(); }, [load]);

  const paypalOptions = useMemo(
    () => ({ "client-id": PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }),
    [],
  );

  if (!data) return <div className="min-h-[100dvh] bg-ink flex items-center justify-center text-cream/40">Loading wallet…</div>;

  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-8" data-testid="wallet-page">
      <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Wallet</p>
      <h1 className="text-cream text-4xl font-black tracking-tighter mt-1">Your Coins</h1>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="crystal-glass rounded-3xl p-5">
          <p className="text-cream/50 text-[10px] tracking-[0.25em] uppercase font-bold">Coins</p>
          <p data-testid="wallet-waves" className="text-cream text-3xl font-black tracking-tighter mt-1">{data.waves}</p>
          <p className="text-cream/40 text-xs mt-1">Spend on gifts, boosts & subs</p>
        </div>
        <div className="crystal-glass rounded-3xl p-5 border border-papaya/30">
          <p className="text-pink-300 text-[10px] tracking-[0.25em] uppercase font-bold">Pearls</p>
          <p data-testid="wallet-pearls" className="text-cream text-3xl font-black tracking-tighter mt-1">{data.pearls}</p>
          <Link to="/earnings" className="text-papaya text-xs font-bold hover:underline mt-1 inline-block">Cash out →</Link>
        </div>
      </div>

      <h2 className="text-cream text-xl font-black tracking-tight mt-8 mb-3">Buy Coins</h2>
      <div className="grid grid-cols-2 gap-3" data-testid="wallet-packages">
        {data.packages.map((p) => (
          <button
            key={p.id}
            data-testid={`pkg-${p.id}`}
            onClick={() => setSelected(p)}
            className={`p-4 rounded-2xl text-left transition-all duration-300 ${selected?.id === p.id ? "bg-papaya text-ink papaya-glow" : "bg-white/5 border border-white/10 text-cream hover:border-papaya/50"}`}
          >
            <p className="text-xs tracking-[0.2em] uppercase font-bold opacity-70">{p.label}</p>
            <p className="text-2xl font-black tracking-tighter mt-1">{p.waves} 🪙</p>
            <p className="text-sm font-bold opacity-80 mt-0.5">${p.usd}</p>
          </button>
        ))}
      </div>

      {selected && PAYPAL_CLIENT_ID && (
        <div className="mt-5">
          <p className="text-cream/60 text-xs mb-2">Pay with PayPal — money goes to Giffwurldaunce.</p>
          <div data-testid="paypal-buttons" className="rounded-2xl overflow-hidden bg-white/95 p-2">
            <PayPalScriptProvider options={paypalOptions}>
              <PayPalButtons
                key={selected.id}
                style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay" }}
                disabled={busy}
                createOrder={async () => {
                  setBusy(true); setMsg("");
                  try {
                    const { data } = await api.post("/wallet/orders", { package_id: selected.id });
                    return data.order_id;
                  } finally { setBusy(false); }
                }}
                onApprove={async (d) => {
                  setBusy(true);
                  try {
                    const { data: cap } = await api.post("/wallet/orders/capture", { order_id: d.orderID });
                    setMsg(`+${cap.waves_credited} Coins added to your wallet 🪙`);
                    await load();
                  } catch (e) {
                    setMsg(e.response?.data?.detail || "Capture failed");
                  } finally { setBusy(false); }
                }}
                onError={(e) => { setMsg("PayPal error: " + (e?.message || "unknown")); }}
              />
            </PayPalScriptProvider>
          </div>
          {msg && <p data-testid="wallet-msg" className="mt-3 text-sm text-cream/80">{msg}</p>}
        </div>
      )}

      <h2 className="text-cream text-xl font-black tracking-tight mt-10 mb-3">Activity</h2>
      <div className="space-y-2" data-testid="wallet-tx">
        {data.transactions.length === 0 && <p className="text-cream/40 text-sm">No activity yet.</p>}
        {data.transactions.map((t) => {
          const meta = TX_LABELS[t.type] || { label: t.type, icon: ArrowsLeftRight, cls: "text-cream" };
          const Icon = meta.icon;
          return (
            <div key={t.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/5">
              <div className={`w-9 h-9 rounded-full bg-white/5 flex items-center justify-center ${meta.cls}`}><Icon size={18} weight="fill" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-cream text-sm font-bold">{meta.label}</p>
                <p className="text-cream/40 text-xs">{new Date(t.created_at).toLocaleString()}</p>
              </div>
              <p className={`text-sm font-black tracking-tight ${t.amount > 0 ? "text-emerald-400" : "text-papaya"}`}>
                {t.amount > 0 ? "+" : ""}{t.amount} {t.currency === "pearls" ? "🪩" : "🪙"}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
